/**
 * 로그 관리 유틸리티
 * 새로고침 후에도 로그를 확인할 수 있도록 localStorage에 저장
 */

import { formatDateTimeKST } from './dateUtils';

interface LogEntry {
    timestamp: string;
    level: 'info' | 'warn' | 'error' | 'debug';
    message: string;
    data?: any;
}

class Logger {
    private static readonly LOG_KEY = 'bi_admin_logs';
    private static readonly MAX_LOGS = 100; // 최대 로그 개수

    /**
     * 로그 저장
     */
    private static saveLog(level: LogEntry['level'], message: string, data?: any): void {
        try {
            const logEntry: LogEntry = {
                timestamp: new Date().toISOString(),
                level,
                message,
                data: data ? JSON.parse(JSON.stringify(data)) : undefined
            };

            // 기존 로그 가져오기
            const existingLogs = this.getLogs();

            // 새 로그 추가
            existingLogs.push(logEntry);

            // 최대 개수 초과 시 오래된 로그 제거
            if (existingLogs.length > this.MAX_LOGS) {
                existingLogs.splice(0, existingLogs.length - this.MAX_LOGS);
            }

            // localStorage에 저장
            localStorage.setItem(this.LOG_KEY, JSON.stringify(existingLogs));
        } catch (error) {
            console.error('로그 저장 실패:', error);
        }
    }

    /**
     * 저장된 로그 가져오기
     */
    static getLogs(): LogEntry[] {
        try {
            const logs = localStorage.getItem(this.LOG_KEY);
            return logs ? JSON.parse(logs) : [];
        } catch (error) {
            console.error('로그 읽기 실패:', error);
            return [];
        }
    }

    /**
     * 로그 출력 및 저장
     */
    static info(message: string, data?: any): void {
        console.log(`[INFO] ${message}`, data);
        this.saveLog('info', message, data);
    }

    static warn(message: string, data?: any): void {
        console.warn(`[WARN] ${message}`, data);
        this.saveLog('warn', message, data);
    }

    static error(message: string, data?: any): void {
        console.error(`[ERROR] ${message}`, data);
        this.saveLog('error', message, data);
    }

    static debug(message: string, data?: any): void {
        console.log(`[DEBUG] ${message}`, data);
        this.saveLog('debug', message, data);
    }

    /**
     * 로그 초기화
     */
    static clearLogs(): void {
        localStorage.removeItem(this.LOG_KEY);
    }

    /**
     * 로그를 콘솔에 출력
     */
    static printLogs(): void {
        const logs = this.getLogs();
        console.group('📋 저장된 로그');
        logs.forEach(log => {
            const timestamp = formatDateTimeKST(log.timestamp);
            console.log(`[${timestamp}] ${log.level.toUpperCase()}: ${log.message}`, log.data);
        });
        console.groupEnd();
    }
}

export default Logger;
